import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
];

const productLinks = [
  { to: "/prodotti/componenti-arredamento", label: "Arredamento" },
  { to: "/prodotti/componenti-nautica", label: "Nautica" },
  { to: "/prodotti/packaging-cosmetico", label: "Beauty" },
  { to: "/prodotti/food-packaging-pet", label: "Packaging" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dropDesktop, setDropDesktop] = useState(false);
  const [dropMobile, setDropMobile] = useState(false);
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => { setOpen(false); setDropDesktop(false); setDropMobile(false); }, [pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  const isProductsActive = pathname.startsWith("/prodotti/") || pathname === "/cosmetica";

  const openDrop = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setDropDesktop(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setDropDesktop(false), 220);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white border-b border-neutral-200/80 shadow-[0_2px_18px_-12px_rgba(0,0,0,0.18)]" data-testid="site-navbar">
      <div className="max-w-[1320px] mx-auto h-[96px] lg:h-[104px] flex items-center justify-between px-5 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/images/logo.png" alt="Drosun Europe" width="260" height="72" fetchpriority="high" decoding="sync" className="h-14 lg:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10" aria-label="Navigazione principale">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `nav-link font-body text-[15px] font-semibold tracking-wide text-drosun-text hover:text-drosun-red transition-colors ${isActive ? "is-active" : ""}`}>
              {l.label.toUpperCase()}
            </NavLink>
          ))}

          <div className="relative" onMouseEnter={openDrop} onMouseLeave={scheduleClose}>
            <button type="button" className={`nav-link font-body text-[15px] font-semibold tracking-wide text-drosun-text hover:text-drosun-red transition-colors flex items-center gap-1 ${isProductsActive ? "is-active" : ""}`} aria-expanded={dropDesktop} aria-haspopup="menu" onClick={() => setDropDesktop((v) => !v)} onFocus={openDrop}>
              PRODOTTI
              <ChevronDown size={16} className={`transition-transform ${dropDesktop ? "rotate-180" : ""}`} />
            </button>
            <div className={`absolute right-0 top-full h-4 w-[260px] ${dropDesktop ? "" : "pointer-events-none"}`} aria-hidden />
            <div role="menu" onMouseEnter={openDrop} onMouseLeave={scheduleClose} className={`absolute right-0 top-[calc(100%+12px)] min-w-[240px] origin-top transition ${dropDesktop ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"}`}>
              <div className="rounded-xl border border-neutral-200 bg-white shadow-xl py-2">
                {productLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="block px-5 py-3 text-[15px] font-semibold text-drosun-text hover:text-drosun-red hover:bg-neutral-50 transition-colors" onClick={() => setDropDesktop(false)}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/contatti" className={({ isActive }) => `nav-link font-body text-[15px] font-semibold tracking-wide text-drosun-text hover:text-drosun-red transition-colors ${isActive ? "is-active" : ""}`}>CONTATTI</NavLink>
        </nav>

        <button className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-drosun-text" onClick={() => setOpen((v) => !v)} aria-label={open ? "Chiudi menu" : "Apri menu"}>
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      <div className={`lg:hidden fixed inset-0 top-[96px] bg-white z-40 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <nav className="flex flex-col px-6 py-6 gap-2" aria-label="Navigazione mobile">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `font-display text-2xl uppercase py-4 border-b border-neutral-200 ${isActive ? "text-drosun-red" : "text-drosun-text"}`}>{l.label}</NavLink>
          ))}
          <div className="py-4 border-b border-neutral-200">
            <button type="button" onClick={() => setDropMobile((v) => !v)} className={`w-full flex items-center justify-between font-display text-2xl uppercase ${isProductsActive ? "text-drosun-red" : "text-drosun-text"}`}>
              Prodotti
              <ChevronDown size={22} className={`transition-transform ${dropMobile ? "rotate-180" : ""}`} />
            </button>
            <div className={`overflow-hidden transition-all ${dropMobile ? "max-h-64 mt-3" : "max-h-0"}`}>
              {productLinks.map((item) => (
                <Link key={item.to} to={item.to} className="block pl-4 py-3 text-lg font-semibold text-drosun-text hover:text-drosun-red">{item.label}</Link>
              ))}
            </div>
          </div>
          <NavLink to="/contatti" className={({ isActive }) => `font-display text-2xl uppercase py-4 border-b border-neutral-200 ${isActive ? "text-drosun-red" : "text-drosun-text"}`}>Contatti</NavLink>
        </nav>
      </div>
    </header>
  );
}
